import { useCallback, useMemo } from 'react';

import { useChartData } from '@/components/Chart/queries/useChartData';
import type { SeriesRecord } from '@/components/Chart/types/chartData';
import { findSeriesBasedOnKey } from '@/components/Chart/utils/findSeriesBasedOnKey';
import { getShortTitle } from '@/components/Chart/utils/getShortTitle';
import { parseDataToChartSeries } from '@/components/Chart/utils/parseDataToChartSeries';
import usePeriodAbsFormatter from '@/hooks/usePeriodAbsFormatter';
import useUserDetails from '@/hooks/useUserDetails';
import { generateMockSeries } from '@/mocks/chartSeries.mock';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import type { TimeSeriesPoint, TimeSeriesPointCustom } from '../utils/buildChartLineSeries';

const useChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  isDhChart = false,
  isDhChartFullscreen = false
) => {
  const { formatPeriodAbs } = usePeriodAbsFormatter();
  const {
    data: chartData,
    isError: isChartDataError,
    isLoading: isChartDataLoading
  } = useChartData(templateChartSeries, 'spot');

  const takeSpotData = useCallback(
    (tsData: SeriesRecord, decimalPlaces: number): TimeSeriesPoint => {
      const x = tsData[0];
      const y = Number(tsData[1]);
      const custom: TimeSeriesPointCustom = {
        periodAbs: tsData.length > 2 ? formatPeriodAbs(tsData[2]) : '',
        decimalPlaces
      };
      return { x, y, custom };
    },
    [formatPeriodAbs]
  );
  const { isUserBasic, isUserExpired } = useUserDetails();

  const chartSeries = useMemo(() => {
    if (isDhChart && (isUserBasic || isUserExpired)) {
      return [generateMockSeries()];
    }
    if (chartData) {
      const dataSymbols = chartData?.symbols;
      return dataSymbols?.flatMap(({ key, metadata, series, transactionDate }, symbolIndex) => {
        const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
        const shortTitle = getShortTitle(templateSeries);
        const parseableElements = {
          color: templateSeries?.settings?.color,
          symbolIndex,
          shortTitle,
          key,
          metadata,
          transactionDate
        };
        return parseDataToChartSeries(series, parseableElements, isDhChartFullscreen, takeSpotData);
      });
    }
  }, [
    isDhChart,
    isUserBasic,
    isUserExpired,
    chartData,
    templateChartSeries,
    isDhChartFullscreen,
    takeSpotData
  ]);

  return {
    loading: isChartDataLoading || !templateChartSeries,
    error: isChartDataError,
    chartSeries
  };
};

export default useChartSeries;
